import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BiTimeFive } from "react-icons/bi";
import Navbar from "../Items/Navbar";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJs,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Legend,
  Tooltip,
} from "chart.js";
import useVerify from "../useVerify";
import moment from "moment";

ChartJs.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Legend,
  Tooltip
);

function HistoryPage() {
  useVerify("user");

  const { hasilId } = useParams();
  const navigate = useNavigate();

  const [hasil, setHasil] = useState({ kolomAnswer: [] });
  const [loading, setLoading] = useState(true);

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Terjawab",
        data: [],
        fill: false,
        backgroundColor: "transparent",
        borderColor: "#FF914D",
        pointBackgroundColor: "#FF914D",
        pointBorderColor: "white",
        pointBorderWidth: 2,
        pointRadius: 5,
        tension: 0.3,
      },
      {
        label: "Benar",
        data: [],
        fill: false,
        backgroundColor: "transparent",
        borderColor: "#22c55e",
        pointBackgroundColor: "#22c55e",
        pointBorderColor: "white",
        pointBorderWidth: 2,
        pointRadius: 5,
        tension: 0.3,
      },
      {
        label: "Salah",
        data: [],
        fill: false,
        backgroundColor: "transparent",
        borderColor: "#ef4444",
        pointBackgroundColor: "#ef4444",
        pointBorderColor: "white",
        pointBorderWidth: 2,
        pointRadius: 5,
        tension: 0.3,
      },
    ],
  });

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
          font: { size: 12 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${context.parsed.y}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: true, color: "#e2e8f0" },
        ticks: { font: { size: 11 } },
      },
      y: {
        min: 0,
        max: 50,
        grid: { color: "#e2e8f0" },
        ticks: { font: { size: 11 } },
      },
    },
  };

  useEffect(() => {
    fetchDetailHasilByHasilId(hasilId);
    // eslint-disable-next-line
  }, [hasilId]);

  useEffect(() => {
    if (hasil && hasil.kolomAnswer && hasil.kolomAnswer.length > 0) {
      const labels = hasil.kolomAnswer.map((_, index) => `Kolom ${index + 1}`);
      const dataTerjawab = hasil.kolomAnswer.map((a) => Number(a.totalJawaban));
      const dataBenar = hasil.kolomAnswer.map((a) => Number(a.benar));
      const dataSalah = hasil.kolomAnswer.map((a) => Number(a.salah));

      setChartData((prev) => ({
        labels,
        datasets: [
          { ...prev.datasets[0], data: dataTerjawab },
          { ...prev.datasets[1], data: dataBenar },
          { ...prev.datasets[2], data: dataSalah },
        ],
      }));
    }
  }, [hasil]);

  async function fetchDetailHasilByHasilId(id) {
    setLoading(true);
    await fetch(`${window.REACT_APP_BASE_URL}/api/hasil/${id}`)
      .then((response) => response.json())
      .then((data) => {
        setHasil(data);
        setLoading(false);
      });
  }

  // Cek apakah grafik benar stabil/meningkat
  const dataBenar = hasil.kolomAnswer.map((a) => Number(a.benar));
  const isGrafікBagus =
    dataBenar.length > 1 &&
    dataBenar.every((val, idx) => idx === 0 || val >= dataBenar[idx - 1] - 2);

  const totalBenar = dataBenar.reduce((a, b) => a + b, 0);
  const totalTerjawab = hasil.kolomAnswer
    .map((a) => Number(a.totalJawaban))
    .reduce((a, b) => a + b, 0);
  const totalSalah = hasil.kolomAnswer
    .map((a) => Number(a.salah))
    .reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen w-full bg-slate-100">
      <Navbar />

      {loading ? (
        <div className="flex justify-center items-center h-64 text-gray-500">
          Memuat data...
        </div>
      ) : (
        <div className="w-full flex flex-col items-center py-10 px-4">
          <div className="w-full max-w-5xl">

            {/* Header */}
            <div className="mb-6">
              <button
                onClick={() => navigate(-1)}
                className="text-sm text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1"
              >
                ← Kembali
              </button>
              <h1 className="font-extrabold text-4xl text-[#FF914D]">
                Detail Hasil
              </h1>
            </div>

            {/* Info & Grafik */}
            <div className="w-full flex flex-col md:flex-row gap-6 mb-6">

              {/* Kiri - Info + Grafik */}
              <div className="w-full md:w-1/2 flex flex-col gap-4">

                {/* Info Card */}
                <div className="bg-white rounded-xl p-4 shadow border border-gray-100">
                  <h3 className="text-2xl font-bold text-slate-800 uppercase">
                    {hasil.namaTest}
                  </h3>
                  <div className="flex items-center gap-1 mt-2 text-gray-500 text-sm">
                    <BiTimeFive />
                    <span>{moment(hasil.createdAt).format("DD MMMM YYYY")}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div className="bg-orange-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Terjawab</p>
                      <p className="text-xl font-bold text-[#FF914D]">{totalTerjawab}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Benar</p>
                      <p className="text-xl font-bold text-green-500">{totalBenar}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">Salah</p>
                      <p className="text-xl font-bold text-red-500">{totalSalah}</p>
                    </div>
                  </div>
                </div>

                {/* Indikator grafik */}
                {dataBenar.length > 1 && (
                  <div
                    className={`rounded-xl p-3 text-sm font-semibold text-center ${
                      isGrafікBagus
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {isGrafікBagus
                      ? "✅ Grafik bagus! Kemampuan stabil atau meningkat"
                      : "⚠️ Terdapat penurunan kemampuan pada beberapa sesi"}
                  </div>
                )}

                {/* Grafik */}
                <div className="bg-white rounded-xl p-4 shadow border border-gray-100">
                  <h4 className="text-sm font-bold text-gray-600 mb-3">
                    Grafik Kemampuan Per Sesi
                  </h4>
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>

              {/* Kanan - Tabel */}
              <div className="w-full md:w-1/2">
                <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          Kolom
                        </th>
                        <th className="px-4 py-3 text-xs font-bold text-center text-gray-500 uppercase">
                          Tabel
                        </th>
                        <th className="px-4 py-3 text-xs font-bold text-center text-gray-500 uppercase">
                          Terjawab
                        </th>
                        <th className="px-4 py-3 text-xs font-bold text-center text-gray-500 uppercase">
                          Benar
                        </th>
                        <th className="px-4 py-3 text-xs font-bold text-center text-gray-500 uppercase">
                          Salah
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {hasil.kolomAnswer.map((kolom, index) => (
                        <tr
                          key={kolom.kolomAnswerId}
                          className="hover:bg-slate-50 transition"
                        >
                          <td className="px-4 py-3 text-sm font-semibold text-gray-700">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 text-xs text-center text-gray-600 uppercase max-w-[120px] truncate">
                            {kolom.kolom}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-gray-700">
                            {kolom.totalJawaban} / 50
                          </td>
                          <td className="px-4 py-3 text-sm text-center font-bold text-green-600">
                            {kolom.benar}
                          </td>
                          <td className="px-4 py-3 text-sm text-center font-bold text-red-500">
                            {kolom.salah}
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    {/* Footer total */}
                    <tfoot className="bg-slate-100">
                      <tr>
                        <td
                          colSpan={2}
                          className="px-4 py-3 text-sm font-bold text-gray-700"
                        >
                          Total
                        </td>
                        <td className="px-4 py-3 text-sm text-center font-bold text-gray-700">
                          {totalTerjawab} / 500
                        </td>
                        <td className="px-4 py-3 text-sm text-center font-bold text-green-600">
                          {totalBenar}
                        </td>
                        <td className="px-4 py-3 text-sm text-center font-bold text-red-500">
                          {totalSalah}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryPage;
